import axiosInstance from './axiosInstance';
import { clearSignupCouponCode, getSignupCouponCode } from './signupCoupon';
import { tokenService } from './tokenService';

export interface LoginResponse {
  access_token: string;
  refresh_token: string;
  signup_credits_awarded?: number;
  is_new_user?: boolean;
  signup_event_id?: string;
  user: {
    id: string;
    email: string;
    username: string;
    display_name: string;
    avatar_url?: string;
    profile_image_url?: string;
    auth_provider: string;
    disposable_email?: boolean;
  };
}

interface RegistrationPendingResponse {
  detail: string;
  disposable_email?: boolean;
}

const completeAuth = (data: LoginResponse): LoginResponse => {
  tokenService.setTokens(data.access_token, data.refresh_token);
  localStorage.removeItem('referral_code');
  clearSignupCouponCode();

  return data;
};

const completePendingEmailSignup = (
  data: RegistrationPendingResponse,
  referralCode: string | null,
): RegistrationPendingResponse => {
  if (referralCode) localStorage.removeItem('referral_code');
  clearSignupCouponCode();

  return data;
};

export const authService = {
  // Google OAuth
  async googleCallback(code: string, state: string): Promise<LoginResponse> {
    const referralCode = localStorage.getItem('referral_code');
    const couponCode = getSignupCouponCode();
    const response = await axiosInstance.post('/api/v1/auth/google/callback', {
      code,
      state,
      redirect_uri: process.env.REACT_APP_GOOGLE_REDIRECT_URI,
      referral_code: referralCode || undefined,
      coupon_code: couponCode || undefined,
    });

    const data = response.data;

    return completeAuth(data);
  },

  // Apple OAuth
  async appleCallback(code: string, state: string): Promise<LoginResponse> {
    const couponCode = getSignupCouponCode();
    const response = await axiosInstance.post('/api/v1/auth/apple/callback', {
      code,
      state,
      redirect_uri: process.env.REACT_APP_APPLE_REDIRECT_URI,
      coupon_code: couponCode || undefined,
    });

    const data = response.data;

    return completeAuth(data);
  },

  // Apple Native Sign-In (iOS app — uses identity token from AuthenticationServices)
  async appleNativeCallback(identityToken: string, authorizationCode?: string, user?: string): Promise<LoginResponse> {
    const referralCode = localStorage.getItem('referral_code');
    const couponCode = getSignupCouponCode();
    const response = await axiosInstance.post('/api/v1/auth/apple/native-signin', {
      identity_token: identityToken,
      authorization_code: authorizationCode,
      user,
      referral_code: referralCode || undefined,
      coupon_code: couponCode || undefined,
    });

    const data = response.data;

    return completeAuth(data);
  },

  // Microsoft OAuth
  async microsoftCallback(code: string, state: string): Promise<LoginResponse> {
    const couponCode = getSignupCouponCode();
    const response = await axiosInstance.post('/api/v1/auth/microsoft/callback', {
      code,
      state,
      redirect_uri: process.env.REACT_APP_MICROSOFT_REDIRECT_URI,
      coupon_code: couponCode || undefined,
    });

    const data = response.data;

    return completeAuth(data);
  },


  // GitHub OAuth
  async githubCallback(code: string, state: string): Promise<LoginResponse> {
    const couponCode = getSignupCouponCode();
    const response = await axiosInstance.post('/api/v1/auth/github/callback', {
      code,
      state,
      redirect_uri: process.env.REACT_APP_GITHUB_REDIRECT_URI,
      coupon_code: couponCode || undefined,
    });

    const data = response.data;

    return completeAuth(data);
  },

  async emailRegister(
    email: string,
    password: string,
    displayName: string,
    country: string,
    stateCode?: string
  ): Promise<RegistrationPendingResponse> {
    const referralCode = localStorage.getItem('referral_code');
    const couponCode = getSignupCouponCode();
    const result = await axiosInstance.post('/api/v1/auth/email/register', {
      email,
      password,
      display_name: displayName,
      country,
      state_code: stateCode || undefined,
      referral_code: referralCode || undefined,
      coupon_code: couponCode || undefined,
    });

    const data = result.data as RegistrationPendingResponse;

    // Coupon attribution has been persisted with the pending verification.
    return completePendingEmailSignup(data, referralCode);
  },

  // Email Login
  async emailLogin(email: string, password: string): Promise<LoginResponse> {
    const response = await axiosInstance.post('/api/v1/auth/email/login', {
      email,
      password,
      remember_me: true,
    });

    const data = response.data;

    return completeAuth(data);
  },

  // Verify Email (returns auth tokens for auto-login)
  async verifyEmail(token: string): Promise<LoginResponse> {
    const response = await axiosInstance.post('/api/v1/auth/email/verify-email', { token });

    const data = response.data;

    return completeAuth(data);
  },

  // Resend Verification Email
  async resendVerificationEmail(email: string): Promise<{ message: string }> {
    return await axiosInstance.post('/api/v1/auth/email/resend-verification', {
      email,
    });
  },

  // Forgot Password
  async forgotPassword(email: string): Promise<{ message: string }> {
    return await axiosInstance.post('/api/v1/auth/email/forgot-password', { email });
  },

  // Reset Password
  async resetPassword(token: string, newPassword: string): Promise<{ message: string }> {
    return await axiosInstance.post('/api/v1/auth/email/reset-password', {
      token,
      new_password: newPassword,
    });
  },

  // Get Current User
  async getCurrentUser() {
    const response = await axiosInstance.get('/api/v1/auth/me');

    const data = response.data;

    // Transform relative profile image URL to absolute URL with token for <img> auth
    const apiBaseUrl = process.env.REACT_APP_API_URL || '';
    const accessToken = tokenService.getAccessToken();
    const tokenParam = accessToken ? `?token=${encodeURIComponent(accessToken)}` : '';
    const profileImageUrl = data.profile_image_url
      ? `${apiBaseUrl}${data.profile_image_url}${tokenParam}`
      : undefined;

    // Map snake_case API response to camelCase User interface
    return {
      id: data.id,
      email: data.email,
      username: data.username,
      firstName: data.first_name,
      lastName: data.last_name,
      displayName: data.display_name,
      avatarUrl: data.avatar_url,
      profileImageUrl,
      emailVerified: data.email_verified ?? true,
      isActive: data.is_active ?? true,
      lastLogin: data.last_login,
      authProvider: data.auth_provider,
      canChangePassword: data.can_change_password,
      canChangeEmail: data.can_change_email,
      system_role: data.system_role || 'user',
      country: data.country,
      stateCode: data.state_code,
      gstNumber: data.gst_number,
      orgName: data.org_name,
      subscription_tier: data.subscription_tier,
      subscription_status: data.subscription_status,
      credits_balance: data.credits_balance,
      hasByokKeys: data.has_byok_keys ?? false,
      disposableEmail: data.disposable_email ?? false,
    };
  },

  // Logout
  async logout(): Promise<void> {
    try {
      await axiosInstance.post('/api/v1/auth/logout');
    } finally {
      tokenService.clearTokens();
    }
  },

  // Delete Account
  async deleteAccount(): Promise<{ message: string }> {
    return await axiosInstance.post('/api/v1/auth/delete-account');
  },
};
